import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/customer/dashboard/BookNewAppointment.css";
import { checkLoggedInCustomer } from "../../../helper/CustomHelper";
import { useNavigate } from "react-router-dom";

/* ───────── SVG ICONS ───────── */

const SgcChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SgcCalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const BookNewAppointment = () => {
  const { baseUrl } = useContext(AppContext);

  const navigate = useNavigate();

  const [treatment, setTreatment] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================
  // VIEW ALL TREATMENTS
  // =========================

  const viewAllTreatments = () => {
    axios
      .post(`${baseUrl}/customer/treatment/viewAllTreatments`, {
        escustomer_id: localStorage.getItem("customerId"),
      })
      .then((response) => {
        console.log("RAW RESPONSE:", response.data);

        const res = response.data;

        const data =
          res?.treatments || res?.data || (Array.isArray(res) ? res : []);

        if (Array.isArray(data)) {
          setTreatments(data);
        } else {
          setTreatments([]);
        }
      })
      .catch((error) => {
        console.log("API ERROR:", error);
        setTreatments([]);

        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Unable to load treatments. Please try again.",
          confirmButtonText: "OK",
        });
      });
  };

  // ====================
  // CREATE APPOINTMENT
  // ====================

  const createAppointment = () => {
    console.log({
      treatment,
      appointmentDate,
      appointmentTime,
    });

    // REQUIRED FIELDS
    if (!treatment || !appointmentDate || !appointmentTime) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill all required fields.",
        confirmButtonText: "OK",
      });

      return;
    }

    setLoading(true);

    const formData = new FormData();

    formData.append("treatment_id", treatment);

    formData.append(
      "escustomer_id",
      JSON.parse(localStorage.getItem("customer")).id,
    );

    formData.append("appointment_date", appointmentDate);
    formData.append("appointment_time", appointmentTime);
    formData.append("notes", notes);

    // SAFE FILE APPEND
    if (file) {
      formData.append("file", file);
    }

    axios
      .post(`${baseUrl}/customer/appointment/createAppointment`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      .then((response) => {
        console.log("SUCCESS:", response.data);

        Swal.fire({
          icon: "success",
          title: "Appointment Booked!",
          text: "Your appointment has been booked successfully.",
          confirmButtonText: "OK",
        });

        // RESET FORM
        setTreatment("");
        setAppointmentDate("");
        setAppointmentTime("");
        setNotes("");
        setFile(null);
      })

      .catch((error) => {
        const status = error.response?.status;
        const msg =
          error.response?.data?.message ||
          "Something went wrong. Please try again.";

        Swal.fire({
          icon: "warning",
          title: status === 409 ? "Slot Unavailable" : "Booking Failed!",
          text: msg,
          confirmButtonText: "OK",
        });
      })

      .finally(() => {
        setLoading(false);
      });
  };

  // ===============
  // USE EFFECT
  // ===============

  useEffect(() => {
    viewAllTreatments();
    checkLoggedInCustomer(navigate);
  }, []);

  return (
    <div className="sgc-book-page">
      <Container fluid>
        <h1 className="sgc-book-title">Book new appointment</h1>

        <p className="sgc-book-subtitle">
          Pick a treatment and we'll handle the rest.
        </p>

        <div className="sgc-book-card">
          {/* TREATMENT */}

          <div className="sgc-book-row">
            <label className="sgc-book-label">Treatment</label>

            <div className="sgc-book-select-wrap">
              <select
                className="sgc-book-select"
                value={treatment}
                onChange={(e) => setTreatment(e.target.value)}
              >
                <option value="">Choose a treatment</option>

                {Array.isArray(treatments) &&
                  treatments.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || item.title}
                    </option>
                  ))}
              </select>

              <SgcChevronDown />
            </div>
          </div>

          {/* DATE & TIME */}

          <div className="sgc-book-row">
            <Row className="g-3">
              {/* DATE */}

              <Col xs={12} md={6}>
                <label className="sgc-book-label">Date</label>

                <div className="sgc-book-date-wrap">
                  <SgcCalendarIcon />

                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="
                      sgc-book-input
                      sgc-book-input-date
                    "
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                  />
                </div>
              </Col>

              {/* TIME */}

              <Col xs={12} md={6}>
                <label className="sgc-book-label">Time</label>

                <div className="sgc-book-select-wrap">
                  <select
                    className="sgc-book-select"
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.target.value)}
                  >
                    <option value="">Pick a time</option>

                    <option value="09:00:00">09:00 AM</option>

                    <option value="10:00:00">10:00 AM</option>

                    <option value="11:00:00">11:00 AM</option>

                    <option value="12:00:00">12:00 PM</option>

                    <option value="13:00:00">01:00 PM</option>

                    <option value="14:00:00">02:00 PM</option>

                    <option value="15:00:00">03:00 PM</option>

                    <option value="16:00:00">04:00 PM</option>

                    <option value="17:00:00">05:00 PM</option>
                  </select>

                  <SgcChevronDown />
                </div>
              </Col>
            </Row>
          </div>

          {/* NOTES */}

          <div className="sgc-book-row">
            <label className="sgc-book-label">Notes / skin concerns</label>

            <textarea
              className="sgc-book-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* FILE */}

          <div className="sgc-book-row">
            <label className="sgc-book-label">Upload Image</label>

            <input
              type="file"
              className="sgc-book-input"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>

          {/* BUTTON */}

          <button
            className="sgc-book-btn"
            onClick={createAppointment}
            disabled={loading}
          >
            {loading ? "Booking..." : "Request appointment"}
          </button>
        </div>
      </Container>
    </div>
  );
};

export default BookNewAppointment;
